import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import Select from "../../../shared/components/Select";
import useApi from "../../../shared/hooks/useApi";
import {
  ALLOWED_SHOW_ON_AGENCY_TYPES,
  ALLOWED_VBP_TYPES,
  ANSWER_TYPE_LABELS,
  CREATABLE_ANSWER_TYPES,
  MULTI_ANSWER_TYPES,
  NursingQuestion,
  NursingQuestionAnswerType,
  NursingQuestionPossibleAnswer,
  NursingQuestionsSettingsResponse,
} from "../nursing-question-settings.consts";
import { emitRefreshPatientQuestionTypes } from "../nursing-question-settings.utils";
import { useUpdateNursingQuestion } from "./NursingQuestionCells";

export type NursingQuestionModalState =
  | { type: "closed" }
  | { type: "new" }
  | { type: "editAlerts"; question: NursingQuestion };

interface Props {
  modalState: NursingQuestionModalState;
  onClose: () => void;
}

export default function NursingQuestionModal({ modalState, onClose }: Props) {
  switch (modalState.type) {
    case "closed":
      return null;
    case "new":
      return <NewQuestionModal onClose={onClose} />;
    case "editAlerts":
      return (
        <EditAlertsModal
          key={modalState.question.id}
          question={modalState.question}
          onClose={onClose}
        />
      );
  }
}

const ANSWER_TYPE_OPTIONS = CREATABLE_ANSWER_TYPES.map((value) => ({
  label: ANSWER_TYPE_LABELS[value],
  value,
}));

const EMPTY_DRAFT_ANSWER: NursingQuestionPossibleAnswer = {
  isTriggeringAlert: false,
  text: "",
};

function NewQuestionModal({ onClose }: { onClose: () => void }) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const [questionText, setQuestionText] = React.useState("");
  const [answerType, setAnswerType] = React.useState<NursingQuestionAnswerType | null>(null);
  const [possibleAnswers, setPossibleAnswers] = React.useState<NursingQuestionPossibleAnswer[]>([]);
  const [draftAnswer, setDraftAnswer] = React.useState(EMPTY_DRAFT_ANSWER);
  const [showOnPatient, setShowOnPatient] = React.useState(false);
  const [showOnAgency, setShowOnAgency] = React.useState(false);
  const [isVBP, setIsVBP] = React.useState(false);
  const [transferAnswerBetweenTasks, setTransferAnswerBetweenTasks] = React.useState(false);
  const [isDraftConfirmOpen, setIsDraftConfirmOpen] = React.useState(false);

  const listKey = queries.nursingQuestionSettings.list().queryKey;

  const createMutation = useMutation({
    ...mutations.nursingQuestionSettings.create(),
    onError: () => {
      toast({
        title: "Something went wrong",
        description: "Could not add question type",
        status: "error",
        position: "top-right",
      });
    },
    onSuccess: (created) => {
      queryClient.setQueryData<NursingQuestionsSettingsResponse>(listKey, (data) =>
        data === undefined ? data : { questions: [...data.questions, created] }
      );
      toast({
        title: "Question type successfully created",
        status: "success",
        position: "top-right",
      });
      emitRefreshPatientQuestionTypes();
      onClose();
    },
  });

  const isMultiAnswerType = answerType !== null && MULTI_ANSWER_TYPES.includes(answerType);
  const isVBPAllowed = answerType !== null && ALLOWED_VBP_TYPES.includes(answerType);
  const isShowOnAgencyAllowed =
    answerType !== null && ALLOWED_SHOW_ON_AGENCY_TYPES.includes(answerType);

  const handleAnswerTypeChange = (value: NursingQuestionAnswerType | undefined) => {
    const nextType = value ?? null;
    setAnswerType(nextType);
    if (nextType === null || !ALLOWED_VBP_TYPES.includes(nextType)) {
      setIsVBP(false);
    }
    if (nextType === null || !ALLOWED_SHOW_ON_AGENCY_TYPES.includes(nextType)) {
      setShowOnAgency(false);
      setPossibleAnswers([]);
      setDraftAnswer(EMPTY_DRAFT_ANSWER);
    }
  };

  const addDraftAnswer = () => {
    if (draftAnswer.text.length === 0) {
      return;
    }
    setPossibleAnswers([...possibleAnswers, draftAnswer]);
    setDraftAnswer(EMPTY_DRAFT_ANSWER);
  };

  const updatePossibleAnswer = (index: number, patch: Partial<NursingQuestionPossibleAnswer>) => {
    setPossibleAnswers(
      possibleAnswers.map((answer, i) => (i === index ? { ...answer, ...patch } : answer))
    );
  };

  const submit = () => {
    if (questionText.trim() === "") {
      toast({ title: "Missing Question text", status: "error", position: "top-right" });
      return;
    }
    if (answerType === null) {
      toast({ title: "Missing Answer type", status: "error", position: "top-right" });
      return;
    }
    if (isMultiAnswerType && possibleAnswers.length === 0) {
      toast({ title: "Missing Possible answers", status: "error", position: "top-right" });
      return;
    }
    if (isMultiAnswerType && draftAnswer.text !== "") {
      setIsDraftConfirmOpen(true);
      return;
    }
    send();
  };

  const send = () => {
    if (answerType === null) {
      return;
    }
    const answers: NursingQuestionPossibleAnswer[] =
      answerType === "yesNo"
        ? [
            { isTriggeringAlert: false, text: "Yes" },
            { isTriggeringAlert: false, text: "No" },
          ]
        : possibleAnswers;

    createMutation.mutate({
      answerType,
      isVBP,
      possibleAnswers: answers,
      questionText,
      showOnAgency,
      showOnPatient,
      transferAnswerBetweenTasks,
    });
  };

  return (
    <Modal isOpen={true} size="xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New question type</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Question text</FormLabel>
              <Textarea
                rows={2}
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Answer type</FormLabel>
              <Select
                allowUnselect={true}
                label="Answer type"
                multiple={false}
                options={ANSWER_TYPE_OPTIONS}
                value={answerType}
                onChange={handleAnswerTypeChange}
              />
            </FormControl>

            {isMultiAnswerType && (
              <FormControl>
                <FormLabel>Possible answers</FormLabel>
                <VStack align="stretch" spacing={2}>
                  {possibleAnswers.map((answer, index) => (
                    <Flex key={index} align="center" gap={2} wrap="wrap">
                      <Text flex={1} fontWeight="medium">
                        {answer.text}
                      </Text>
                      <Checkbox
                        isChecked={answer.isTriggeringAlert}
                        onChange={(e) =>
                          updatePossibleAnswer(index, { isTriggeringAlert: e.target.checked })
                        }
                      >
                        Triggers alert
                      </Checkbox>
                      {answer.isTriggeringAlert && (
                        <Input
                          placeholder="Alert text"
                          value={answer.alertText ?? ""}
                          width="40"
                          onChange={(e) =>
                            updatePossibleAnswer(index, { alertText: e.target.value })
                          }
                        />
                      )}
                      <IconButton
                        aria-label="Remove answer"
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setPossibleAnswers(possibleAnswers.filter((_, i) => i !== index))
                        }
                      />
                    </Flex>
                  ))}
                  <HStack>
                    <Input
                      placeholder="New possible answer"
                      value={draftAnswer.text}
                      onChange={(e) => setDraftAnswer({ ...draftAnswer, text: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addDraftAnswer();
                        }
                      }}
                    />
                    <IconButton
                      aria-label="Add answer"
                      icon={<AddIcon />}
                      isDisabled={draftAnswer.text.length === 0}
                      onClick={addDraftAnswer}
                    />
                  </HStack>
                </VStack>
              </FormControl>
            )}

            <VStack align="stretch" spacing={2}>
              <Checkbox
                isChecked={showOnPatient}
                onChange={(e) => setShowOnPatient(e.target.checked)}
              >
                Show on Patient
              </Checkbox>
              <Checkbox
                isChecked={showOnAgency}
                isDisabled={!isShowOnAgencyAllowed}
                onChange={(e) => setShowOnAgency(e.target.checked)}
              >
                Show on Agency
              </Checkbox>
              <Checkbox
                isChecked={isVBP}
                isDisabled={!isVBPAllowed}
                onChange={(e) => setIsVBP(e.target.checked)}
              >
                Is for VBP
              </Checkbox>
              <Checkbox
                isChecked={transferAnswerBetweenTasks}
                onChange={(e) => setTransferAnswerBetweenTasks(e.target.checked)}
              >
                Transfer Answer Between Tasks
              </Checkbox>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" isLoading={createMutation.isPending} onClick={submit}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog
        isOpen={isDraftConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDraftConfirmOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Are you sure?</AlertDialogHeader>
            <AlertDialogBody>New possible answer has not saved yet</AlertDialogBody>
            <AlertDialogFooter gap={3}>
              <Button ref={cancelRef} variant="ghost" onClick={() => setIsDraftConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={() => {
                  setIsDraftConfirmOpen(false);
                  send();
                }}
              >
                Yes, submit without
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
}

function EditAlertsModal(props: { question: NursingQuestion; onClose: () => void }) {
  const update = useUpdateNursingQuestion(props.question);
  const [answers, setAnswers] = React.useState<NursingQuestionPossibleAnswer[]>(
    props.question.possibleAnswers ?? []
  );

  const updateAnswer = (index: number, patch: Partial<NursingQuestionPossibleAnswer>) => {
    setAnswers(answers.map((answer, i) => (i === index ? { ...answer, ...patch } : answer)));
  };

  const save = () => {
    update.mutate({ possibleAnswers: answers }, { onSuccess: props.onClose });
  };

  return (
    <Modal isOpen={true} size="xl" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Alerts</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            <Text color="gray.600">{props.question.questionText}</Text>
            {answers.map((answer, index) => (
              <Flex key={index} align="center" gap={2} wrap="wrap">
                <Text flex={1} fontWeight="medium">
                  {answer.text}
                </Text>
                <Checkbox
                  isChecked={answer.isTriggeringAlert}
                  onChange={(e) => updateAnswer(index, { isTriggeringAlert: e.target.checked })}
                >
                  Triggers alert
                </Checkbox>
                {answer.isTriggeringAlert && (
                  <Input
                    placeholder="Alert text"
                    value={answer.alertText ?? ""}
                    width="40"
                    onChange={(e) => updateAnswer(index, { alertText: e.target.value })}
                  />
                )}
              </Flex>
            ))}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" isLoading={update.isPending} onClick={save}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
